import { useState, useCallback } from 'react';
import { throttle } from 'lodash';
import { MapLayerMouseEvent } from 'react-map-gl';
import {
  FWELAYER,
  INSITU_EXPANDED_LAYER,
  INSITU_LAYER,
  INSITU_UPLOAD_LAYER
} from "../Layer/MarkerLayer";
import {normalizeLongitude} from "../../../../utils/normalizedLongitude";

export type LayerId =
  | typeof FWELAYER
  | typeof INSITU_LAYER
  | typeof INSITU_UPLOAD_LAYER
  | typeof INSITU_EXPANDED_LAYER
  | 'icon-transparent'
  | 'icon-layer';

export interface LayerInfo {
  layer?: {
    id: LayerId;
  };
  object?: {
    active: boolean;
    coordinates: [number, number];
    [key: string]: any;
  };
  coordinate?: number[];
}

interface UseMapInteractionsProps {
  setActivePopupPoint: (layerId: LayerId) => void;
  onPopupCoordinatesChanged?: (object: any, layerId: LayerId) => void;
  dragPan: boolean;
  isAddMarkerMode: boolean;
}

interface UseMapInteractionsReturn {
  isHoverFweInsitu: boolean;
  mouseCoords?: [number, number]
  hoverInfoActivePoint: any | null;
  handleMouseMove: (e: MapLayerMouseEvent) => void;
  handleHover: (info: any) => void;
  getCursor: () => string
  handleClick: (info: any, ev: any, coords?:[number, number], onClick?: (coordinate: number[]) => void, isAddMarkerMode?: boolean) => void;
}

export function useMapInteractions(props: UseMapInteractionsProps): UseMapInteractionsReturn {
  const {
    setActivePopupPoint,
    onPopupCoordinatesChanged,
    isAddMarkerMode,
    dragPan
  } = props;

  const [isHoverFweInsitu, setIsHoverFweInsitu] = useState<boolean>(false);
  const [hoverInfoActivePoint, setHoverInfoActivePoint] = useState<any | null>(null);
  const [mouseCoords, setMouseCoords] = useState<[number, number]>()

  const handleMouseMove = useCallback((e: MapLayerMouseEvent): void => {
    if (e.lngLat) {
      const lng = normalizeLongitude(e.lngLat.lng)
      const normalizedLng = Math.round(lng * 10000) / 10000
      const lat: number = Math.round(e.lngLat.lat * 10000) / 10000;
      setMouseCoords([normalizedLng, lat])
    }
  }, [setMouseCoords]);

  const throttledHoverHandler = throttle((
    info: LayerInfo,
    setHoverState: React.Dispatch<React.SetStateAction<boolean>>,
    setPointState: React.Dispatch<React.SetStateAction<any | null>>,
    dragPanEnabled: boolean
  ): void => {
    const layerId = info?.layer?.id;
    const isActivePoint = (
      (layerId === 'icon-transparent' || layerId === 'icon-layer') &&
      info?.object?.active
    );

    setHoverState(
      layerId === INSITU_LAYER ||
      layerId === FWELAYER ||
      layerId === INSITU_EXPANDED_LAYER
    );

    if (isActivePoint && dragPanEnabled) {
      setPointState(info?.object);
    } else {
      setPointState(null);
    }
  }, 100);

  const handleHover = useCallback((info: LayerInfo): void => {
    throttledHoverHandler(
      info,
      setIsHoverFweInsitu,
      setHoverInfoActivePoint,
      dragPan
    );
  }, [dragPan, throttledHoverHandler]);

  const handleClick = useCallback((
    info: LayerInfo,
    ev: any,
    coords?: [number, number],
    onClick?: (coordinate: number[]) => void,
    isAddMarkerMode?: boolean
  ): void => {
    if (ev) {
      if (isAddMarkerMode && !isHoverFweInsitu && onClick && coords) {
        onClick(coords);
      } else {
        const object = info?.object;
        const layerId = info?.layer?.id;

        if (
          layerId === FWELAYER ||
          layerId === INSITU_LAYER ||
          layerId === INSITU_UPLOAD_LAYER ||
          layerId === INSITU_EXPANDED_LAYER
        ) {
          setActivePopupPoint(layerId);

          if (onPopupCoordinatesChanged && object) {
            onPopupCoordinatesChanged(object, layerId);
          }
        }
      }
    }
  }, [setActivePopupPoint, onPopupCoordinatesChanged, isHoverFweInsitu]);

  const getCursor = () => {
    if (isAddMarkerMode || isHoverFweInsitu) {
      return 'pointer';
    } else if (hoverInfoActivePoint && dragPan) {
      return 'grab';
    } else if (!dragPan){
      return 'grabbing';
    }
    return 'default';
  };

  return {
    isHoverFweInsitu,
    hoverInfoActivePoint,
    handleMouseMove,
    handleHover,
    handleClick,
    mouseCoords,
    getCursor
  };
}
